import React, { useRef, useState } from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import SortIcon from '@mui/icons-material/Sort';
import { Badge, Box, Button, ClickAwayListener, IconButton, Popper, Tooltip } from '@mui/material';

import { classes } from './styles';
import { WHITE } from '../../../../ThemeContext/constants';

import type { Dispatch, ReactNode } from 'react';

import type { TableFilter, TableFilterAction } from '../types';

const Filters = ({
  dispatch,
  numberOfFilters,
  label,
  children,
  keepMounted = false,
  leadingClickAway = false
}: {
  dispatch: Dispatch<TableFilterAction<TableFilter>>;
  numberOfFilters: number;
  label: string;
  children: ReactNode;
  keepMounted?: boolean;
  leadingClickAway?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorEl = useRef<HTMLButtonElement>(null);

  const handleDropdownOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const handleApplyFilters = () => {
    dispatch({ type: 'SET_FILTERS' });
    setIsOpen(false);
  };

  const handleResetFilters = () => {
    dispatch({ type: 'RESET_FILTERS' });
  };

  // Popper portals its content to document.body by default, so it is never a
  // real DOM descendant of a ClickAwayListener wrapping the trigger button --
  // every click inside the (portaled) panel would be misread as "away" and
  // close it immediately. Per MUI's own Popper+ClickAwayListener composition
  // pattern, the listener instead wraps just the portaled content; ignoring
  // clicks on the trigger itself avoids an immediate close-then-reopen when
  // the trigger's own onClick toggles isOpen back on.
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (anchorEl.current?.contains(event.target as Node)) return;
    setIsOpen(false);
  };

  return (
    <Box>
      <Badge
        badgeContent={numberOfFilters}
        sx={{ '& .MuiBadge-badge': { backgroundColor: '#E37D7A', color: WHITE } }}
      >
        <Tooltip arrow placement="top" title="Filter">
          <IconButton
            ref={anchorEl}
            data-testid={`filter-dropdown-${label}-button`}
            id={`filter-dropdown-${label}-button`}
            onClick={handleDropdownOpen}
            sx={isOpen ? { ...classes.iconButton, ...classes.iconButtonOpen } : classes.iconButton}
          >
            <SortIcon />
          </IconButton>
        </Tooltip>
      </Badge>
      <Popper
        anchorEl={anchorEl.current}
        id="column-settings-popover"
        keepMounted={keepMounted}
        open={isOpen}
        placement="bottom-end"
        sx={classes.popover}
      >
        <ClickAwayListener
          mouseEvent={leadingClickAway ? 'onMouseDown' : 'onClick'}
          onClickAway={handleClickAway}
          touchEvent={leadingClickAway ? 'onTouchStart' : 'onTouchEnd'}
        >
          <Box sx={classes.filterPopoverContent}>
            <Box sx={classes.scrollableFilters}>
              <Box sx={classes.filterPopoverHeader}>
                <Button
                  endIcon={<CancelIcon />}
                  id="reset-filters-button"
                  onClick={handleResetFilters}
                  sx={classes.clearButton}
                  variant="contained"
                >
                  Clear all
                </Button>
                <Button
                  id="apply-filters-button"
                  onClick={handleApplyFilters}
                  sx={classes.applyButton}
                  variant="contained"
                >
                  Apply
                </Button>
              </Box>
              {children}
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default Filters;
